import {
  type Classroom,
  gradeOverview,
  type HomeworkAssignment,
  type HomeworkRecord,
  type Student,
} from "@classroom/domain";
import { classStudents } from "./classSharing";

export function needsCourseworkFollowup(row: { pending: number; missing: number; unrecorded: number }) {
  return row.pending > 0 || row.missing > 0 || row.unrecorded > 0;
}

export function courseworkClassSummary(
  classes: Classroom[],
  students: Student[],
  assignments: HomeworkAssignment[],
  records: HomeworkRecord[],
  date: string,
) {
  return classes
    .filter((item) => item.active && item.classRole === "teaching")
    .flatMap((classroom) => {
      const roster = classStudents(classroom, classes, students);
      const count = courseworkSummary(classroom.id, roster, assignments, records, date).filter(
        needsCourseworkFollowup,
      ).length;
      return count ? [{ classroom, count }] : [];
    });
}

export function courseworkSummary(
  classId: string,
  students: Student[],
  assignments: HomeworkAssignment[],
  records: HomeworkRecord[],
  date: string,
  passLine?: number,
) {
  const overview = gradeOverview(classId, students, assignments, records);
  return overview.assignments.map(({ assignment }) => {
    const entries = overview.students.flatMap((row) =>
      row.entries.filter((entry) => entry.assignment.id === assignment.id),
    );
    const hasMaximum =
      assignment.maxScore !== undefined && Number.isFinite(assignment.maxScore) && assignment.maxScore > 0;
    const scored = entries.filter((entry) => entry.percentage !== undefined && entry.record?.status !== "missing");
    const percentages = scored.map((entry) => entry.percentage ?? 0);
    const pending = entries.filter(
      (entry) =>
        hasMaximum &&
        assignment.dueDate <= date &&
        entry.record &&
        (entry.record.status === "complete" || entry.record.status === "late") &&
        entry.record.score === undefined,
    ).length;
    const missing =
      assignment.dueDate <= date ? entries.filter((entry) => entry.record?.status === "missing").length : 0;
    const unrecorded = assignment.dueDate <= date ? entries.filter((entry) => !entry.record).length : 0;
    const below =
      passLine !== undefined &&
      Number.isFinite(passLine) &&
      passLine >= 0 &&
      passLine <= 100 &&
      assignment.dueDate <= date
        ? percentages.filter((percentage) => percentage < passLine).length
        : 0;
    return {
      assignment,
      entries,
      scored: scored.length,
      pending,
      missing,
      unrecorded,
      below,
      average: scored.length ? percentages.reduce((sum, percentage) => sum + percentage, 0) / scored.length : undefined,
      distribution: [0, 40, 60, 80].map((min, index) => ({
        label: ["0–39%", "40–59%", "60–79%", "80–100%"][index],
        count: percentages.filter(
          (percentage) => percentage >= min && (index === 3 || percentage < [40, 60, 80][index]),
        ).length,
      })),
    };
  });
}
